// Общие элементы экранов стройки: шахматка дома, карточка работы.
import type React from "react";

import { fmtDate, fmtNum } from "../shared/format";
import { floorName, floorShort, STATUS_RU } from "./ctx";

export type GridData = { grips: string[]; rows: { floor: number; cycle: number; cycle_id: string; status: string; cells: { grip: string; status: string; pct?: number | null; tasks?: any[] }[] }[] };

/** Шахматка «этажи × захватки» (С — стены, П — перекрытие). */
export function Chessboard({ data, current, compact, onCell, maxFloors }: {
  data: GridData; current?: number | null; compact?: boolean; onCell?: (floor: number, grip: string) => void; maxFloors?: number;
}) {
  const walls = data.grips.filter((g) => g.startsWith("С"));
  const slabs = data.grips.filter((g) => !g.startsWith("С"));
  let rows = data.rows;
  if (maxFloors && current) {
    const top = Math.min(Math.max(...rows.map((r) => r.floor)), current + 3);
    rows = rows.filter((r) => r.floor <= top && r.floor > top - maxFloors);
  }
  const cols = `36px repeat(${walls.length}, auto) 8px repeat(${slabs.length}, auto)`;
  return (
    <div className={`chess${compact ? " compact" : ""}`} style={{ gridTemplateColumns: cols }} role="grid" aria-label="Шахматка этажи по захваткам">
      <span />
      {walls.map((g) => <span key={g} className="gh">{g}</span>)}
      <span className="gap" />
      {slabs.map((g) => <span key={g} className="gh">{g}</span>)}
      {rows.map((r) => {
        const cell = (g: string) => {
          const c = r.cells.find((x) => x.grip === g);
          const st = c?.status || "none";
          return (
            <button key={g} className={`cell ${st}`} title={`${floorName(r.floor)}, ${g}: ${STATUS_RU[st] || "—"}${c?.pct !== undefined && c?.pct !== null ? `, ${fmtNum(c.pct, 0)}%` : ""}`}
              onClick={() => onCell?.(r.floor, g)} aria-label={`${floorName(r.floor)}, ${g}, ${STATUS_RU[st] || "нет работ"}`}>
              {!compact && c?.pct ? `${fmtNum(c.pct, 0)}` : ""}
            </button>
          );
        };
        return [
          <span key={`f${r.floor}`} className={`fl${r.floor === current ? " cur" : ""}`}>{floorShort(r.floor)}</span>,
          ...walls.map(cell),
          <span key={`g${r.floor}`} className="gap" />,
          ...slabs.map(cell),
        ];
      })}
    </div>
  );
}

export function TaskLine({ t }: { t: any }) {
  const tone = t.status === "done" ? "s-done" : t.deviation === "critical" ? "s-conflict" : t.deviation === "behind" ? "s-loading" : t.status === "in_progress" ? "s-planned" : "";
  return (
    <div className="row" style={{ padding: "8px 0", borderBottom: "1px solid var(--line-soft)" }}>
      <span className="grow">
        <b>{t.grip}</b> · {t.work}
        <div className="small muted">план {fmtDate(t.planned_start)}–{fmtDate(t.planned_end)}{t.forecast_end && t.status !== "done" ? `, прогноз ${fmtDate(t.forecast_end)}` : ""}{t.fact_volume ? `, объём ${fmtNum(t.fact_volume)} ${t.unit || ""}` : ""}</div>
      </span>
      <span className="num">{fmtNum(t.fact_percent, 0)}% <span className="muted small">/ {fmtNum(t.planned_percent, 0)}%</span></span>
      <span className={`tag ${tone}`}>{t.status === "done" ? "выполнено" : STATUS_RU[t.deviation] || STATUS_RU[t.status]}</span>
      {t.lag_days > 0 && t.status !== "done" && <span className="small">−{fmtNum(t.lag_days)} дн.</span>}
    </div>
  );
}

/** Зона захватки на схеме в долях: рамка {x, y, w, h} или контур любой формы — points (встреча 24.10.2026). */
export type Zone = { x: number; y: number; w: number; h: number; points?: [number, number][] };

export function GripScheme({ url, zone, label, others = [], children }: {
  url: string; zone?: Zone | null; label?: string; others?: { code: string; zone: Zone }[]; children?: React.ReactNode;
}) {
  const pct = (z: Zone) => ({ left: `${z.x * 100}%`, top: `${z.y * 100}%`, width: `${z.w * 100}%`, height: `${z.h * 100}%` });
  const poly = (z: Zone) => z.points!.map(([x, y]) => `${x},${y}`).join(" ");
  const shapes = [...others.map((o) => ({ ...o, other: true })), ...(zone ? [{ code: label || "", zone, other: false }] : [])];
  return (
    <div className="scheme">
      <img src={url} alt="План этажа" draggable={false} />
      {shapes.some((s) => s.zone.points) && (
        <svg className="zone-svg" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">
          {shapes.filter((s) => s.zone.points).map((s) => <polygon key={s.code} className={s.other ? "other" : ""} points={poly(s.zone)} vectorEffect="non-scaling-stroke" />)}
        </svg>
      )}
      {shapes.map((s) => (s.zone.points
        ? s.code ? <span key={s.code} className="zone-label" style={{ left: `${s.zone.x * 100}%`, top: `${s.zone.y * 100}%` }}>{s.code}</span> : null
        : <div key={s.code} className={`zone${s.other ? " other" : ""}`} style={pct(s.zone)}>{s.code && <span>{s.code}</span>}</div>))}
      {children}
    </div>
  );
}
